import i18n from '../i18n';
/**
 * Shared search + sort toolbar used by the Project detail tabs
 * (Tasks, Checklists, Test Reports, Damage, Documents, Surveys) so every
 * list behaves the same way: free-text search on the left, sort dropdown
 * on the right, newest-first by default.
 */
import React from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, ArrowUpDown, X } from 'lucide-react';
import { WeekFilter } from './ui/week-filter';
export { matchesWeek } from '../lib/weekFilter';

// Sort options every list shares. Spread these first so "Newest first"
// is the top entry in each dropdown.
export const CREATED_SORT_OPTIONS = [
  { value: 'created_desc', label: i18n.t('auto.lbl.newestFirst') },
  { value: 'created_asc', label: i18n.t('auto.lbl.oldestFirst') },
];

export const SearchSortBar = ({
  search,
  onSearch,
  sortBy,
  onSort,
  options,
  placeholder = 'Search…',
  testIdPrefix,
  className = '',
  week,
  onWeek,
}) => (
  <div className={`flex items-center gap-2 flex-wrap ${className}`}>
    <div className="relative flex-1 min-w-[180px]">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
      <Input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={placeholder}
        className={`h-8 pl-8 text-xs ${search ? 'pr-8' : ''}`}
        data-testid={`${testIdPrefix}-search`}
      />
      {search ? (
        <button
          type="button"
          onClick={() => onSearch('')}
          aria-label={i18n.t('auto.misc2.clearSearch')}
          title={i18n.t('auto.misc2.clearSearch')}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          data-testid={`${testIdPrefix}-search-clear`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
    {onWeek && <WeekFilter value={week} onChange={onWeek} testId={`${testIdPrefix}-week-filter`} />}
    <Select value={sortBy} onValueChange={onSort}>
      <SelectTrigger className="h-8 w-[196px] text-xs flex-shrink-0" data-testid={`${testIdPrefix}-sort`}>
        <span className="flex items-center gap-1.5 min-w-0 whitespace-nowrap overflow-hidden">
          <ArrowUpDown className="h-3 w-3 text-gray-400 flex-shrink-0" />
          <span className="truncate"><SelectValue /></span>
        </span>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={`${testIdPrefix}-sort-${o.value}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// True when the query is empty or any of the supplied field values
// contains it (case-insensitive substring match).
export const matchesQuery = (q, values) => {
  const needle = String(q || '').trim().toLowerCase();
  if (!needle) return true;
  return values.some((v) => String(v ?? '').toLowerCase().includes(needle));
};

// Comparator over an ISO-ish `created_at`. String compare is safe here:
// ISO 8601 sorts lexicographically. Missing dates fall to the bottom.
export const byCreated = (dir = 'desc') => (a, b) => {
  const av = String(a?.created_at || '');
  const bv = String(b?.created_at || '');
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
};

// Case-insensitive ascending compare on a string field.
export const byText = (fn) => (a, b) =>
  String(fn(a) || '').localeCompare(String(fn(b) || ''), undefined, { sensitivity: 'base' });
